/**
 * Version Check Service
 * 
 * Periodically checks the API version and compares it with the local frontend version.
 * If versions don't match, it notifies the application to display a version mismatch warning.
 * 
 * @author autodata-frontend
 * @date 2025-11-07
 */

import axios from 'axios';
import Utils from './Utils';
import { version } from '../autobuild_version';

export interface VersionCheckConfig {
  onVersionMismatch: (localVersion: string, apiVersion: string) => void;
  checkIntervalMs?: number; // Default: 3600000 (1 hour)
}

export class VersionCheckService {
  private intervalId: NodeJS.Timeout | null = null;
  private config: VersionCheckConfig;
  private checkIntervalMs: number;
  private isChecking = false;

  constructor(config: VersionCheckConfig) {
    this.config = config;
    this.checkIntervalMs = config.checkIntervalMs || 3600000; // Default: 1 hour
  }

  /**
   * Start the version checking service
   * Performs an immediate check and then schedules periodic checks
   */
  public start(): void {
    if (this.intervalId) {
      Utils.warn('VersionCheckService is already running');
      return;
    }

    // Perform initial check
    this.checkVersion();

    // Schedule periodic checks
    this.intervalId = setInterval(() => {
      this.checkVersion();
    }, this.checkIntervalMs);

    Utils.log(`VersionCheckService started (checking every ${this.checkIntervalMs / 1000 / 60} minutes)`);
  }

  /**
   * Stop the version checking service
   */
  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      Utils.log('VersionCheckService stopped');
    }
  }

  /**
   * Manually trigger a version check
   */
  public async checkVersion(): Promise<void> {
    if (this.isChecking) {
      Utils.log('Version check already in progress, skipping...');
      return;
    }

    this.isChecking = true;

    try {
      const apiBaseUrl = `${Utils.URL_ADDRESS}${Utils.DOTNET_DEV_SERVICE}`;
      const versionEndpoint = `${apiBaseUrl}/v1/version`;

      Utils.log(`Checking version from: ${versionEndpoint}`);

      const response = await axios.get(versionEndpoint, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Content-Type': 'text/plain',
        },
      });

      // API returns plain text version string
      if (response.data) {
        const apiVersion = typeof response.data === 'string' ? response.data.trim() : response.data.version;
        const localVersion = version;
        
        // Convert version strings to comparable numbers
        const localVersionNum = this.versionToNumber(localVersion);
        const apiVersionNum = this.versionToNumber(apiVersion);
        
        Utils.log(`Version check: Local=${localVersion} (${localVersionNum}), API=${apiVersion} (${apiVersionNum})`);

        if (localVersionNum < apiVersionNum) {
          Utils.warn(`Version mismatch detected! Local: ${localVersion}, API: ${apiVersion}`);
          this.config.onVersionMismatch(localVersion, apiVersion);
        } else {
          Utils.log('Version check: Versions match');
        }
      } else {
        Utils.warn('Version check: Invalid response format from API', response.data);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        Utils.error('Version check failed (Axios error):', error.message);
        if (error.response) {
          Utils.error('Response status:', error.response.status);
          Utils.error('Response data:', error.response.data);
        }
      } else {
        Utils.error('Version check failed (Unknown error):', error);
      }
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Update the check interval
   * @param intervalMs - New interval in milliseconds
   */
  public updateInterval(intervalMs: number): void {
    this.checkIntervalMs = intervalMs;
    if (this.intervalId) {
      this.stop();
      this.start();
    }
  }

  /**
   * Convert version string to number for comparison
   * @param versionStr - Version string in format "major.minor.patch"
   * @returns Numeric representation of version
   */
  private versionToNumber(versionStr: string): number {
    try {
      const parts = versionStr.split('.').map(part => parseInt(part, 10));
      
      // Handle different version formats
      if (parts.length === 3) {
        // Convert to: major * 1000000 + minor * 1000 + patch
        return parts[0] * 1000000 + parts[1] * 1000 + parts[2];
      } else if (parts.length === 2) {
        // Format: major.minor (e.g., "3.2")
        return parts[0] * 1000000 + parts[1] * 1000;
      } else if (parts.length === 1) {
        // Format: major (e.g., "3")
        return parts[0] * 1000000;
      }
      
      Utils.warn(`Unexpected version format: ${versionStr}`);
      return 0;
    } catch (error) {
      Utils.error(`Failed to parse version string: ${versionStr}`, error);
      return 0;
    }
  }
}
